import React, { useMemo, useState } from 'react';
import { Button } from '../../../../ui/components/Button';
import { Input } from '../../../../ui/components/Input';
import { Spinner } from '../../../../ui/components/Spinner';
import { cn } from '../../../../ui/utils/cn';

interface CoffeeTypeSelectorProps {
    loadingCoffeeTypes: boolean;
    // pre-sorted (favorites first → rated desc → never-rated alphabetical) by /types/with-counts
    coffeeTypes: any[];
    iconUrls: Record<string, string>;
    selectedCoffeeType: string;
    onSelect: (coffeeKey: string) => void;
    coffeeTypesCache: Record<string, any>;
    showOtherInput: boolean;
    coffeeTypeSelected: boolean;
    onChooseAgain: () => void;
}

const CoffeeTypeCard: React.FC<{
    coffee: any;
    iconUrl: string;
    ratingCount: number;
    isFavorite: boolean;
    isSelected: boolean;
    showCountBadge: boolean;
    onClick: () => void;
}> = ({ coffee, iconUrl, ratingCount, isFavorite, isSelected, showCountBadge, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={cn(
            "relative flex flex-col items-center justify-center p-2 rounded-xl transition-all duration-200",
            "border-2 cursor-pointer min-h-[100px]",
            isSelected
                ? "border-mint bg-mint/15"
                : "border-gray-100 bg-white/80 hover:bg-mint/10 hover:border-mint/30",
            isFavorite && !isSelected && "border-amber-200 bg-amber-50/50"
        )}
    >
        <img
            src={iconUrl || ''}
            alt={coffee.displayName}
            className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
            onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
        />
        <span className="text-[10px] text-gray-600 text-center leading-tight mt-1.5 line-clamp-2">{(coffee.displayName || '').toLowerCase()}</span>
        {showCountBadge && ratingCount > 0 && (
            <span className="absolute top-1 right-1 text-[9px] text-gray-500 bg-white/80 rounded-full px-1.5 py-[1px] border border-gray-200">
                ×{ratingCount}
            </span>
        )}
    </button>
);

interface SectionProps {
    title: string;
    items: any[];
    iconUrls: Record<string, string>;
    selectedCoffeeType: string;
    onSelect: (key: string) => void;
}

const Section: React.FC<SectionProps> = ({ title, items, iconUrls, selectedCoffeeType, onSelect }) => {
    if (items.length === 0) return null;
    return (
        <div className="space-y-2">
            <h4 className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</h4>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                {items.map((coffee) => (
                    <CoffeeTypeCard
                        key={coffee.coffeeKey}
                        coffee={coffee}
                        iconUrl={iconUrls[coffee.coffeeKey]}
                        ratingCount={coffee.userRatingCount ?? 0}
                        isFavorite={!!coffee.isFavorite}
                        isSelected={selectedCoffeeType === coffee.coffeeKey}
                        showCountBadge={(coffee.userRatingCount ?? 0) > 0}
                        onClick={() => onSelect(coffee.coffeeKey)}
                    />
                ))}
            </div>
        </div>
    );
};

const matchesQuery = (coffee: any, q: string): boolean => {
    if (!q) return true;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const haystack = [coffee.displayName, coffee.fullName, coffee.coffeeKey]
        .filter(Boolean)
        .map((s: string) => s.toLowerCase());
    return haystack.some(h => h.includes(needle));
};

const CoffeeTypeSelector: React.FC<CoffeeTypeSelectorProps> = ({
    loadingCoffeeTypes,
    coffeeTypes,
    iconUrls,
    selectedCoffeeType,
    onSelect,
    coffeeTypesCache,
    showOtherInput,
    coffeeTypeSelected,
    onChooseAgain,
}) => {
    const [query, setQuery] = useState('');

    // Bucket the pre-sorted list into the three sections, applying live filter.
    // The API order is preserved within each bucket (rated by count desc, never-rated alphabetical).
    const { favorites, rated, neverRated } = useMemo(() => {
        const favs: any[] = [];
        const rat: any[] = [];
        const never: any[] = [];
        for (const coffee of coffeeTypes) {
            if (!matchesQuery(coffee, query)) continue;
            const count = coffee.userRatingCount ?? 0;
            if (coffee.isFavorite) favs.push(coffee);
            else if (count > 0) rat.push(coffee);
            else never.push(coffee);
        }
        return { favorites: favs, rated: rat, neverRated: never };
    }, [coffeeTypes, query]);

    const hasAnyResults = favorites.length + rated.length + neverRated.length > 0;

    return (
        <div className="space-y-3">
            <label className="text-xs text-gray-500 flex items-center gap-1">
                <span>coffee type</span>
                <span className="text-red-400">*</span>
            </label>

            {!coffeeTypeSelected ? (
                <>
                    {loadingCoffeeTypes ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Spinner size="sm" />
                            <span>Loading...</span>
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <Input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="search coffee types"
                                    className="pr-8"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => setQuery('')}
                                        aria-label="clear search"
                                        className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>

                            {hasAnyResults ? (
                                <div className="space-y-4">
                                    <Section
                                        title="top 5 favorites"
                                        items={favorites}
                                        iconUrls={iconUrls}
                                        selectedCoffeeType={selectedCoffeeType}
                                        onSelect={onSelect}
                                    />
                                    <Section
                                        title="all rated"
                                        items={rated}
                                        iconUrls={iconUrls}
                                        selectedCoffeeType={selectedCoffeeType}
                                        onSelect={onSelect}
                                    />
                                    <Section
                                        title="never rated"
                                        items={neverRated}
                                        iconUrls={iconUrls}
                                        selectedCoffeeType={selectedCoffeeType}
                                        onSelect={onSelect}
                                    />
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 py-4 text-center">
                                    no coffee types match "{query}"
                                </div>
                            )}
                        </>
                    )}
                </>
            ) : (
                <div className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-mint/10">
                    <div className="flex items-center gap-4">
                        <img
                            src={iconUrls[selectedCoffeeType] || ''}
                            alt={coffeeTypesCache[selectedCoffeeType]?.displayName || selectedCoffeeType}
                            className="w-16 h-16 object-contain"
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                        />
                        <span className="font-medium text-ink">{coffeeTypesCache[selectedCoffeeType]?.fullName || selectedCoffeeType}</span>
                    </div>
                    <Button
                        type="button"
                        variant="soft"
                        size="sm"
                        onClick={onChooseAgain}
                    >
                        Change
                    </Button>
                </div>
            )}

            {selectedCoffeeType && !showOtherInput && !coffeeTypeSelected && (
                <div className="text-sm text-muted-foreground">
                    Selected: <strong className="text-foreground">{coffeeTypesCache[selectedCoffeeType] ? coffeeTypesCache[selectedCoffeeType].fullName : selectedCoffeeType}</strong>
                </div>
            )}
        </div>
    );
};

export default CoffeeTypeSelector;
